import { createStore } from '../../lib/store';
import type { BookSummary, PersonRef, SeriesMembership } from '../models';
import { userScopeKey } from '../scope';
import { clearAllRows, putScopeRows, readScopeRows, replaceScopeRows } from './db';

// In-memory mirror of the `plume-library` IndexedDB rows, per scope. Screens
// read from memory; writes go to both. `libraryCacheStore` bumps a version
// per scope so hooks know to re-query.

export const libraryCacheStore = createStore<{ versions: Record<string, number> }>({ versions: {} });

const memory = new Map<string, Map<string, BookSummary>>();
const loading = new Map<string, Promise<Map<string, BookSummary>>>();

function bump(scope: string): void {
  libraryCacheStore.set((s) => ({ versions: { ...s.versions, [scope]: (s.versions[scope] ?? 0) + 1 } }));
}

/** Loads a scope from IndexedDB once (IndexedDB failures yield an empty, memory-only cache). */
export function loadScope(scope: string): Promise<Map<string, BookSummary>> {
  const hit = memory.get(scope);
  if (hit) return Promise.resolve(hit);
  const pending = loading.get(scope);
  if (pending) return pending;
  const promise = readScopeRows(scope)
    .catch(() => [] as BookSummary[])
    .then((rows) => {
      const existing = memory.get(scope);
      if (existing) return existing;
      const map = new Map(rows.map((b) => [b.id, b] as const));
      memory.set(scope, map);
      // Hooks subscribed to the store re-query now that the rows are readable.
      if (map.size > 0) bump(scope);
      return map;
    })
    .finally(() => loading.delete(scope));
  loading.set(scope, promise);
  return promise;
}

/** `<userScopeKey>|`: the prefix of every library scope of the signed-in account, or null when signed out. */
function accountScopePrefix(): string | null {
  const key = userScopeKey();
  return key === null ? null : `${key}|`;
}

/** Synchronous view of a scope already in memory. */
export function peekScope(scope: string): Map<string, BookSummary> | undefined {
  return memory.get(scope);
}

const hasIds = <T extends { id: string }>(list: readonly T[]): boolean => list.some((x) => !!x.id);

function mergePeople(existing: PersonRef[], incoming: PersonRef[]): PersonRef[] {
  if (incoming.length === 0) return existing;
  if (hasIds(incoming) || !hasIds(existing)) return incoming;
  // Minified list items carry names only; keep ids learned from full items.
  return incoming.map((p) => existing.find((e) => e.name.toLowerCase() === p.name.toLowerCase()) ?? p);
}

function mergeSeries(existing: SeriesMembership[], incoming: SeriesMembership[]): SeriesMembership[] {
  if (incoming.length === 0) return existing;
  if (hasIds(incoming) || !hasIds(existing)) return incoming;
  return incoming.map((s) => {
    const known = existing.find((e) => e.name.toLowerCase() === s.name.toLowerCase());
    return known ? { ...known, sequence: s.sequence ?? known.sequence } : s;
  });
}

/**
 * Merges a fresh copy over a cached one: technical metadata keeps the
 * incoming value only when present (Flutter `saveBooks`), and author/series
 * ids survive minified payloads that only carry names.
 */
export function mergeCachedBook(existing: BookSummary | undefined, incoming: BookSummary): BookSummary {
  if (!existing) return incoming;
  return {
    ...incoming,
    authors: mergePeople(existing.authors, incoming.authors),
    series: mergeSeries(existing.series, incoming.series),
    bitrate: incoming.bitrate ?? existing.bitrate,
    codec: incoming.codec ?? existing.codec,
    container: incoming.container ?? existing.container,
    sizeBytes: incoming.sizeBytes ?? existing.sizeBytes,
    numChapters: incoming.numChapters || existing.numChapters,
    description: incoming.description || existing.description,
  };
}

/** Upserts books into a scope (memory + IndexedDB). */
export async function upsertScopeBooks(scope: string, books: readonly BookSummary[]): Promise<void> {
  if (books.length === 0) return;
  const map = await loadScope(scope);
  const merged = books.map((b) => mergeCachedBook(map.get(b.id), b));
  for (const b of merged) map.set(b.id, b);
  bump(scope);
  await putScopeRows(scope, merged).catch(() => undefined);
}

/** Replaces a scope's books (full sync). */
export async function replaceScopeBooks(scope: string, books: readonly BookSummary[]): Promise<void> {
  const map = await loadScope(scope);
  const merged = books.map((b) => mergeCachedBook(map.get(b.id), b));
  map.clear();
  for (const b of merged) map.set(b.id, b);
  bump(scope);
  await replaceScopeRows(scope, merged).catch(() => undefined);
}

/** Every cached book of a scope (loads it first). */
export async function getCachedBooks(scope: string): Promise<BookSummary[]> {
  return [...(await loadScope(scope)).values()];
}

/**
 * A cached book from any loaded scope (Book Details seeds, quick-action
 * enrichment). `scopePrefix` (e.g. `${userScopeKey()}|`) limits the lookup to
 * one account's libraries: rows of another account carry its progress.
 */
export function findCachedBook(bookId: string, scopePrefix?: string): BookSummary | undefined {
  for (const [scope, map] of memory) {
    if (scopePrefix && !scope.startsWith(scopePrefix)) continue;
    const hit = map.get(bookId);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Updates a book wherever the signed-in account has it cached (after loading
 * full details, or an optimistic quick action). The copy carries that
 * account's progress, so other accounts' rows are left alone unless
 * `scopePrefix` names them. Unknown books are ignored.
 */
export async function updateCachedBook(book: BookSummary, scopePrefix: string | null = accountScopePrefix()): Promise<void> {
  if (scopePrefix === null) return;
  const writes: Array<Promise<void>> = [];
  for (const [scope, map] of memory) {
    if (!scope.startsWith(scopePrefix)) continue;
    const existing = map.get(book.id);
    if (!existing) continue;
    const merged = mergeCachedBook(existing, book);
    map.set(book.id, merged);
    bump(scope);
    writes.push(putScopeRows(scope, [merged]).catch(() => undefined));
  }
  await Promise.all(writes);
}

/**
 * Stores author / series ids learned from item details on every cached copy
 * of the book (metadata is the same for every account; progress is not
 * touched).
 */
export async function saveCachedBookIds(bookId: string, ids: Pick<BookSummary, 'authors' | 'series'>): Promise<void> {
  const writes: Array<Promise<void>> = [];
  for (const [scope, map] of memory) {
    const existing = map.get(bookId);
    if (!existing) continue;
    const authors = mergePeople(existing.authors, ids.authors);
    const series = mergeSeries(existing.series, ids.series);
    if (authors === existing.authors && series === existing.series) continue;
    const next = { ...existing, authors, series };
    map.set(bookId, next);
    bump(scope);
    writes.push(putScopeRows(scope, [next]).catch(() => undefined));
  }
  await Promise.all(writes);
}

/** Drops every cached library (memory + IndexedDB). */
export async function clearLibraryCache(): Promise<void> {
  const scopes = [...memory.keys()];
  memory.clear();
  scopes.forEach(bump);
  await clearAllRows().catch(() => undefined);
}

/** Test hook. */
export function __resetLibraryMemoryForTests(): void {
  memory.clear();
  loading.clear();
  libraryCacheStore.set({ versions: {} });
}
